import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { AppLogo, type App } from "./AppLogo";
import { useLoopTime } from "./useLoopTime";

/**
 * A small call (two tiles, no faces) beside the Intakall sidebar, for the Features boxes. Someone's line
 * types out under the call, their tile lights up while they talk, then a card pops into the sidebar,
 * drawn as the product draws it. Loops; viewers who turn motion off see the finished moment, still.
 */

// ms from the start of a loop.
export const CALL_SKETCH_TIMELINE = { sayStart: 500, sayEnd: 3000, card: 3500, loop: 9000 } as const;

const SB_HEAD = "font-['Space_Grotesk',sans-serif]";
export const SB_MONO = "font-['JetBrains_Mono',monospace]";

const TILES = [
  { name: "Maya", initial: "M", tint: "bg-[#3b4a6b]" },
  { name: "Jon", initial: "J", tint: "bg-[#4b3b5f]" },
] as const;

export function CallSketch({
  speaker,
  said,
  app,
  label,
  children,
}: {
  speaker: (typeof TILES)[number]["name"];
  said: string;
  app: App;
  label: string;
  /** The card's body, under its logo and label. */
  children: ReactNode;
}) {
  const t = useLoopTime(CALL_SKETCH_TIMELINE.loop);
  const { sayStart, sayEnd } = CALL_SKETCH_TIMELINE;
  const share = Math.min(1, Math.max(0, (t - sayStart) / (sayEnd - sayStart)));
  const typed = said.slice(0, Math.floor(share * said.length));
  const showCard = t >= CALL_SKETCH_TIMELINE.card;

  return (
    <div className="flex h-[220px] w-full max-w-[400px] overflow-hidden rounded-xl border border-char bg-[#111] font-['DM_Sans',sans-serif] shadow-offset-sm">
      <div data-testid="sketch-call" className="flex w-[42%] flex-col gap-1.5 p-2">
        <p className="text-[9px] text-white/70">Weekly sync</p>
        {TILES.map(({ name, initial, tint }) => (
          <div
            key={name}
            data-testid="sketch-tile"
            className={cn(
              "relative flex flex-1 items-center justify-center rounded-md bg-[#2a2a2e]",
              name === speaker && t >= sayStart && t < sayEnd + 400 && "ring-2 ring-[#3B82F6]",
            )}
          >
            <span className={cn("flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold text-white", tint)}>
              {initial}
            </span>
            <span className="absolute bottom-1 left-1.5 text-[8.5px] text-white/80">{name}</span>
          </div>
        ))}
        {/* The full line sits invisibly underneath, so nothing moves as it types. */}
        <div className="relative rounded bg-black/70 px-1.5 py-1 text-[8.5px] leading-[1.35] text-white">
          <p className="invisible">
            <b>{speaker}</b> {said}
          </p>
          <p className="absolute inset-x-1.5 top-1">
            <b>{speaker}</b> <span data-testid="sketch-said">{typed}</span>
          </p>
        </div>
      </div>

      <div data-testid="sketch-sidebar" className="flex flex-1 flex-col bg-[#1a1a2e] px-2 py-2 text-[#e8eaed]">
        <div className="mb-2 flex justify-center">
          <img src="/sidebar-logo.png" alt="" className="h-[11px]" />
        </div>
        {showCard ? (
          <div
            data-testid="sketch-card"
            className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-2 py-1.5 motion-safe:animate-card-slide"
          >
            <div className="mb-0.5 flex items-center gap-1.5">
              <span className="w-4 [&_svg]:h-3 [&_svg]:w-3">
                <AppLogo app={app} />
              </span>
              <span className={cn("text-[9px] font-bold uppercase tracking-[0.06em] text-[#3B82F6]", SB_HEAD)}>{label}</span>
            </div>
            <div className="pl-[22px]">{children}</div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-1.5">
            <div className="flex gap-1">
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="h-1 w-1 rounded-full bg-white opacity-40 motion-safe:animate-dot-bounce"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
            <p className="text-[9px]">Listening for topics...</p>
          </div>
        )}
      </div>
    </div>
  );
}
